import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogTrigger,
} from './ui/dialog';
import { Button } from './ui/button';

interface EditFileDialogProps {
  trigger: React.ReactNode;
  currentName: string;
  onRename: (newName: string) => Promise<void> | void;
  onDelete: () => Promise<void> | void;
}

const EditFileDialog: React.FC<EditFileDialogProps> = ({
  trigger,
  currentName,
  onRename,
  onDelete,
}) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(currentName);
  const [confirmDelete, setConfirmDelete] = useState('');
  const [busy, setBusy] = useState(false);

  const handleRename = async () => {
    if (!name.trim() || name.trim() === currentName) {
      setOpen(false);
      return;
    }
    try {
      setBusy(true);
      await onRename(name.trim());
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (confirmDelete !== 'DELETE') return;
    try {
      setBusy(true);
      await onDelete();
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={o => {
        setOpen(o);
        if (o) {
          setName(currentName);
          setConfirmDelete('');
        }
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <div className='flex items-center justify-between'>
            <DialogTitle>Edit File</DialogTitle>
            <DialogClose asChild>
              <button className='text-muted-foreground hover:text-foreground'>
                ✕
              </button>
            </DialogClose>
          </div>
        </DialogHeader>
        <div className='space-y-4'>
          <div>
            <label className='mb-1 block text-sm text-muted-foreground'>
              File name
            </label>
            <input
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring'
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </div>
          <div className='flex justify-end gap-2'>
            <Button
              variant='secondary'
              type='button'
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleRename} disabled={busy || !name.trim()}>
              Save
            </Button>
          </div>
          <div className='h-px bg-border' />
          <div className='space-y-2'>
            <div className='text-sm'>
              Type{' '}
              <span className='font-mono bg-muted px-1 rounded'>DELETE</span> to
              confirm deletion
            </div>
            <input
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring'
              placeholder='DELETE'
              value={confirmDelete}
              onChange={e => setConfirmDelete(e.target.value)}
            />
            <div className='flex justify-end'>
              <Button
                variant='destructive'
                onClick={handleDelete}
                disabled={busy || confirmDelete !== 'DELETE'}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditFileDialog;
